import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useAppContext } from "../AppContext";
import { updateDb } from "../services/updateDb";
import authHeader from "../services/auth-header";
import { API_BASE_URL } from "./config";
import { categoryIDtoLabel, getCategoryValue } from "./helpers";
import { MODAL_TYPES } from "../modals/modalTypes";

/**
 * Hook that checks whether all measures for SoA categories are implemented
 * and prompts the user via a modal to mark the SoA control as implemented.
 */
const useSoaCheck = () => {
  const { t, i18n } = useTranslation();
  const { currentOrg, openModal } = useAppContext();

  const checkSoaAfterMeasureUpdate = useCallback(async (affectedCategories) => {
    if (!currentOrg?.id) return;

    try {
      // Fetch latest risks and org data in parallel
      const [risksRes, orgRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/risks/byOrg/${currentOrg.id}`, { headers: authHeader() }),
        fetch(`${API_BASE_URL}/api/organizations/${currentOrg.id}`, { headers: authHeader() })
      ]);

      const allRisks = await risksRes.json();
      const orgData = await orgRes.json();

      if (!Array.isArray(allRisks) || !orgData) return;

      // Collect category values to check
      let categoriesToCheck = affectedCategories;
      if (!categoriesToCheck) {
        const categorySet = new Set();
        allRisks.forEach(risk => {
          risk.measures?.forEach(measure => {
            if (measure.category && measure.status !== "dismissed") {
              measure.category.forEach(cat => categorySet.add(getCategoryValue(cat)));
            }
          });
        });
        categoriesToCheck = Array.from(categorySet);
      }

      for (const categoryValue of categoriesToCheck) {
        // Get current SoA state for this category
        const [firstKey, ...rest] = categoryValue.split('.');
        const secondKey = rest.join('.');
        const currentSoa = orgData?.soa?.[firstKey]?.[secondKey] || {};

        // Skip if already implemented
        if (currentSoa.implemented) continue;

        // Collect all non-dismissed measures for this category
        const measuresForCategory = [];
        allRisks.forEach(risk => {
          risk.measures?.forEach(measure => {
            if (measure.category && measure.status !== "dismissed") {
              measure.category.forEach(cat => {
                if (getCategoryValue(cat) === categoryValue) {
                  measuresForCategory.push(measure);
                }
              });
            }
          });
        });

        // Only prompt if there are measures and ALL are implemented
        if (measuresForCategory.length === 0) continue;
        if (!measuresForCategory.every(m => m.status === "implemented")) continue;

        // Show the modal with an onConfirm callback
        const label = categoryIDtoLabel(categoryValue, i18n.language, currentOrg.securityStandard);

        openModal(MODAL_TYPES.SOA_IMPLEMENTED, {
          category: categoryValue,
          categoryLabel: label,
          measureCount: measuresForCategory.length,
          onConfirm: async () => {
            await updateDb(`/api/organizations/soa/${currentOrg.id}`, {
              soaId: categoryValue,
              implemented: true
            });
          }
        });

        // Only show one modal at a time (first match wins)
        break;
      }
    } catch (error) {
      console.error("Error checking SoA status:", error);
    }
  }, [currentOrg, t, i18n, openModal]);

  return { checkSoaAfterMeasureUpdate };
};

export default useSoaCheck;
